import React from 'react';

import { Box, Paper, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';
import { TooltipProps } from 'recharts';
import {
    ValueType,
    NameType,
} from 'recharts/src/component/DefaultTooltipContent';

import { useAccount } from '@contexts/AccountProvider';

enum DataKey {
    paid = 'paid',
    overdue = 'overdue',
}
const Tooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    const { theme } = useTheme();
    const { program } = useAccount();
    const currency = program?.currency_numeric_code;
    if (active && !!payload?.length) {
        let paidHeading = (
            <FormattedMessage
                id="DelinquencyLegend.paid"
                defaultMessage="Paid"
            />
        );
        const overdueAmount = payload.find(
            (payload) => payload.dataKey === DataKey.overdue,
        )?.value;
        const paidAmount = payload.find(
            (payload) => payload.dataKey === DataKey.paid,
        )?.value;
        if (overdueAmount === 0 && paidAmount > 0) {
            paidHeading = (
                <FormattedMessage
                    id="DelinquencyLegend.paidFull"
                    defaultMessage="Overdue amount paid"
                />
            );
        } else if (paidAmount > 0) {
            paidHeading = (
                <FormattedMessage
                    id="DelinquencyLegend.paidPart"
                    defaultMessage="Part paid"
                />
            );
        }
        return (
            <Paper
                sx={{ p: 2.5, boxShadow: '0px 1px 20px rgba(0, 0, 0, 0.15)' }}
                elevation={0}>
                <Typography variant="body2">{paidHeading}</Typography>
                <Box
                    component="span"
                    data-testid="paid-tooltip-dot"
                    sx={{
                        '&::before': {
                            content: '"•"',
                            color: theme.palette.success.dark,
                            fontSize: theme.typography.h5.fontSize,
                            marginRight: '3px',
                        },
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}>
                    <CurrencyFormatter
                        currency={currency}
                        amount={+paidAmount}
                    />
                </Box>
                {!!overdueAmount && (
                    <>
                        <Typography variant="body2" mt={1.375}>
                            <FormattedMessage
                                id="DelinquencyTooltip.overdue"
                                defaultMessage="Overdue amount"
                            />
                        </Typography>
                        <Box
                            component="span"
                            data-testid="paid-tooltip-dot"
                            sx={{
                                '&::before': {
                                    content: '"•"',
                                    color: theme.palette.error.dark,
                                    fontSize: theme.typography.h5.fontSize,
                                    marginRight: '3px',
                                },
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}>
                            <CurrencyFormatter
                                currency={currency}
                                amount={+overdueAmount}
                            />
                        </Box>
                    </>
                )}
            </Paper>
        );
    }
    return null;
};

export default Tooltip;
